"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Colors ────────────────────────────────────────────────────────

const C = {
  index: COLOR.emerald,
  concentrated: COLOR.rose,
  broad: COLOR.sky,
  mixed: COLOR.amber,
};

const STRINGS: Record<
  Locale,
  {
    funds: string[];
    benchmark: string;
    year: (n: number) => string;
    aboveIndex: string;
    belowIndex: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    funds: ["テーマ集中型", "株式以外も混合型"],
    benchmark: "指数（ベンチマーク）",
    year: (n) => `${n}年`,
    aboveIndex: "指数超え",
    belowIndex: "指数割れ",
    title: "アクティブの成果は、戦略タイプで大きく変わる",
    description:
      "同じアクティブファンドでも、少数集中型・広く分散型・株式以外も混ぜる型で値動きの出方は別物です。指数を超えることも、下回ることもあります（値は概念的な概算）。",
  },
  en: {
    funds: ["Theme-concentrated", "Mixed with non-equities"],
    benchmark: "Index (benchmark)",
    year: (n) => `Yr ${n}`,
    aboveIndex: "Beat the index",
    belowIndex: "Behind the index",
    title: "Active results vary a great deal by strategy type",
    description:
      "Even among active funds, a concentrated approach, a broadly diversified one, and one mixing in non-equities behave quite differently. They can beat the index and they can fall behind it (figures are conceptual approximations).",
  },
};

// ── Data (値は概念的な概算) ────────────────────────────────────────

const INDEX_VALUES = [100, 106, 112, 119, 126, 134, 142, 150, 159, 169, 179];

type Fund = {
  color: string;
  values: number[];
};

const FUNDS: Fund[] = [
  {
    color: C.concentrated,
    values: [100, 115, 150, 96, 114, 132, 122, 158, 178, 198, 230],
  },
  {
    color: C.mixed,
    values: [100, 103, 120, 112, 115, 140, 145, 150, 155, 160, 150],
  },
];

// ── Illustration ──────────────────────────────────────────────────

function ActiveVsIndexIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const chartL = 36;
  const chartR = 410;
  const chartT = 14;
  const chartB = 150;
  const chartW = chartR - chartL;
  const chartH = chartB - chartT;

  const pts = INDEX_VALUES.length;
  const minV = 88;
  const maxV = 228;

  const toX = (i: number) => chartL + (i / (pts - 1)) * chartW;
  const toY = (v: number) => chartB - ((v - minV) / (maxV - minV)) * chartH;

  const pathOf = (vs: number[]) => vs.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");

  const indexEnd = INDEX_VALUES[pts - 1];

  const legendItems = [
    { label: t.benchmark, color: C.index, solid: true },
    ...FUNDS.map((f, i) => ({ label: t.funds[i]!, color: f.color, solid: false })),
  ];

  const legendTop = chartB + 34;
  const rowH = 14;
  const legendRows = Math.ceil(legendItems.length / 2);
  const VH = legendTop + legendRows * rowH + 8;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Y gridlines */}
      {[100, 150, 200].map((v) => (
        <g key={v}>
          <line
            x1={chartL}
            y1={toY(v)}
            x2={chartR}
            y2={toY(v)}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="3 6"
          />
          <text x={chartL - 6} y={toY(v) + 3} textAnchor="end" fontSize="7" fill="currentColor" fillOpacity={0.4}>
            {v}
          </text>
        </g>
      ))}

      {/* X axis */}
      <line x1={chartL} y1={chartB} x2={chartR} y2={chartB} stroke="currentColor" strokeOpacity={0.18} />
      {[0, 2, 4, 6, 8, 10].map((yr) => (
        <text
          key={yr}
          x={toX(yr)}
          y={chartB + 14}
          textAnchor="middle"
          fontSize="7"
          fill="currentColor"
          fillOpacity={0.4}
        >
          {t.year(yr)}
        </text>
      ))}

      {/* Active fund lines (dashed) */}
      {FUNDS.map((fund, fundIdx) => {
        const endX = toX(pts - 1);
        const endY = toY(fund.values[pts - 1]);
        return (
          <g key={t.funds[fundIdx]}>
            <path
              d={pathOf(fund.values)}
              fill="none"
              stroke={fund.color}
              strokeWidth="1.5"
              strokeDasharray="5 3"
              opacity={0.9}
            />
            <circle cx={endX} cy={endY} r={3} fill="white" stroke={fund.color} strokeWidth="1.4" />
          </g>
        );
      })}

      {/* Index line (solid, drawn on top) */}
      <path d={pathOf(INDEX_VALUES)} fill="none" stroke={C.index} strokeWidth="2.2" />
      <circle cx={toX(pts - 1)} cy={toY(indexEnd)} r={3.5} fill="white" stroke={C.index} strokeWidth="1.8" />

      {/* Outperform / underperform callouts on the right edge */}
      {(() => {
        const calloutX = chartR + 2;
        const topFund = FUNDS.find((f) => f.values[pts - 1] > indexEnd);
        const botFund = FUNDS.reduce((lo, f) => (f.values[pts - 1] < lo.values[pts - 1] ? f : lo), FUNDS[0]);
        const topY = topFund ? toY(topFund.values[pts - 1]) : toY(indexEnd);
        const botY = toY(botFund.values[pts - 1]);
        const refY = toY(indexEnd);
        return (
          <g>
            <line
              x1={calloutX}
              y1={topY + 3}
              x2={calloutX}
              y2={refY - 3}
              stroke={C.concentrated}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            <path
              d={`M ${calloutX - 3} ${topY + 6} L ${calloutX} ${topY + 3} L ${calloutX + 3} ${topY + 6}`}
              fill="none"
              stroke={C.concentrated}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            <text x={calloutX + 5} y={(topY + refY) / 2 + 2} fontSize="7.5" fill={C.concentrated} fontWeight="600">
              {t.aboveIndex}
            </text>
            <line
              x1={calloutX}
              y1={refY + 3}
              x2={calloutX}
              y2={botY - 3}
              stroke={C.mixed}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            <path
              d={`M ${calloutX - 3} ${botY - 6} L ${calloutX} ${botY - 3} L ${calloutX + 3} ${botY - 6}`}
              fill="none"
              stroke={C.mixed}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            <text x={calloutX + 5} y={(refY + botY) / 2 + 2} fontSize="7.5" fill={C.mixed} fontWeight="600">
              {t.belowIndex}
            </text>
          </g>
        );
      })()}

      {/* Legend block (below chart) */}
      {(() => {
        const col1X = 60;
        const col2X = 260;
        return (
          <g>
            {legendItems.map((item, idx) => {
              const col = idx % 2;
              const row = Math.floor(idx / 2);
              const rx = col === 0 ? col1X : col2X;
              const ry = legendTop + row * rowH;
              return (
                <g key={item.label}>
                  <line
                    x1={rx}
                    y1={ry}
                    x2={rx + 18}
                    y2={ry}
                    stroke={item.color}
                    strokeWidth={item.solid ? 2.2 : 1.5}
                    strokeDasharray={item.solid ? undefined : "5 3"}
                  />
                  <text x={rx + 24} y={ry + 3} fontSize="8.5" fill={item.color} fontWeight="700">
                    {item.label}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })()}
    </svg>
  );
}

// ── Export ────────────────────────────────────────────────────────

export function DocActiveVsIndexDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard title={t.title} description={t.description}>
      <ActiveVsIndexIllustration />
    </IllustrationCard>
  );
}
